import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  Grid,
  IconButton,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Fade,
  Slide,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  AppBar,
  Toolbar,
  Badge
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  ShoppingBag as ShoppingBagIcon,
  LocalShipping as ShippingIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Receipt as ReceiptIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  ShoppingCart as ShoppingCartIcon,
  Person as PersonIcon,
  AccountCircle as AccountCircleIcon
} from '@mui/icons-material';
import { gsap } from 'gsap';
import API_CONFIG from '../../config/api.config';
import customerAuthService from '../../services/customerAuthService';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import useCustomerSessionTimeout from '../../hooks/useCustomerSessionTimeout';
import { formatCurrency } from '../../utils/currencyFormatter';

// ScrollTrigger will be registered if available
if (typeof window !== 'undefined' && window.gsap) {
  try {
    const { ScrollTrigger } = require('gsap/ScrollTrigger');
    gsap.registerPlugin(ScrollTrigger);
  } catch (e) {
    // ScrollTrigger not available, animations will work without it
    console.log('ScrollTrigger not available, using basic animations');
  }
}

const CustomerOrdersPage = () => {
  const { websitePath } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [websiteData, setWebsiteData] = useState(null);

  // Animation refs
  const headerRef = useRef(null);
  const ordersRef = useRef(null);

  // Session timeout tracking
  useCustomerSessionTimeout();

  useEffect(() => {
    const currentCustomer = customerAuthService.getCurrentCustomer();
    if (!currentCustomer) {
      navigate(`/${websitePath}`);
      return;
    }

    fetchWebsiteData();
    fetchOrders();

    // Animate entrance
    gsap.fromTo(headerRef.current,
      { opacity: 0, y: -30 },
      { opacity: 1, y: 0, duration: 0.6, ease: 'power3.out' }
    );
  }, [websitePath, navigate]);

  // Filter orders based on search query
  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredOrders(orders);
    } else {
      const query = searchQuery.toLowerCase().trim();
      const filtered = orders.filter(order => {
        const orderNumber = (order.orderNumber || '').toLowerCase();
        return orderNumber.includes(query);
      });
      setFilteredOrders(filtered);
    }
  }, [searchQuery, orders]);

  useEffect(() => {
    if (filteredOrders.length > 0 && ordersRef.current) {
      // Animate order cards with staggered entrance
      const cards = document.querySelectorAll('.order-card');
      cards.forEach((card, index) => {
        gsap.fromTo(card,
          { opacity: 0, y: 50, scale: 0.95 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 0.6,
            delay: index * 0.1,
            ease: 'back.out(1.2)'
          }
        );
      });
    }
  }, [filteredOrders]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = customerAuthService.getToken();
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/public/website/${websitePath}/orders`,
        {
          headers: {
            'Authorization': token ? `Bearer ${token}` : '',
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }

      const data = await response.json();
      if (data.success) {
        // Sort orders by date (newest first)
        const sortedOrders = (data.orders || []).sort((a, b) => {
          const dateA = new Date(a.orderDate || a.createdAt);
          const dateB = new Date(b.orderDate || b.createdAt);
          return dateB - dateA;
        });
        setOrders(sortedOrders);
        setFilteredOrders(sortedOrders);
      } else {
        throw new Error(data.message || 'Failed to fetch orders');
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case 'DELIVERED':
        return <CheckCircleIcon sx={{ color: 'success.main' }} />;
      case 'PENDING':
      case 'PROCESSING':
        return <PendingIcon sx={{ color: 'warning.main' }} />;
      case 'SHIPPED':
        return <ShippingIcon sx={{ color: 'info.main' }} />;
      case 'CANCELLED':
        return <CancelIcon sx={{ color: 'error.main' }} />;
      default:
        return <PendingIcon sx={{ color: 'text.secondary' }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'DELIVERED':
        return 'success';
      case 'PENDING':
      case 'PROCESSING':
        return 'warning';
      case 'SHIPPED':
        return 'info';
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  

  const fetchWebsiteData = async () => {
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/public/website/${websitePath}/config`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setWebsiteData(data.website);
        }
      }
    } catch (err) {
      console.error('Error fetching website data:', err);
    }
  };

  const getProductName = (item) => {
    // Try multiple possible fields for product name
    if (item.productName) return item.productName;
    if (item.product && item.product.name) return item.product.name;
    if (item.productName) return item.productName;
    return 'Product';
  };

  // Create theme
  const theme = createTheme({
    palette: {
      primary: { main: '#1a237e' },
      secondary: { main: '#c51162' }
    }
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ minHeight: '100vh', bgcolor: '#f5f7fa' }}>
        {/* Topbar Header */}
        <AppBar position="static" sx={{ bgcolor: 'white', boxShadow: 2, color: 'text.primary' }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => navigate(`/${websitePath}`)}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1 }}>
              {websiteData?.logoUrl && (
                <Box
                  component="img"
                  src={websiteData.logoUrl}
                  alt={websiteData.companyName || 'Logo'}
                  sx={{ height: 40, width: 40, objectFit: 'contain', borderRadius: 1 }}
                />
              )}
              <Typography variant="h6" fontWeight={700} sx={{ flexGrow: 1 }}>
                {websiteData?.companyName || 'My Orders'}
              </Typography>
            </Box>
            <IconButton
              color="inherit"
              onClick={() => navigate(`/${websitePath}/cart`)}
              sx={{ mr: 1 }}
            >
              <Badge badgeContent={0} color="primary">
                <ShoppingCartIcon />
              </Badge>
            </IconButton>
            <IconButton
              color="inherit"
              onClick={() => navigate(`/${websitePath}/profile`)}
            >
              <AccountCircleIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Page Header */}
          <Box ref={headerRef} sx={{ mb: 4 }}>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              My Orders
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              View and track all your orders
            </Typography>
            
            {/* Search and Order Count */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search by order number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ maxWidth: 400 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Chip
                label={`${filteredOrders.length} ${filteredOrders.length === 1 ? 'Order' : 'Orders'}`}
                color="primary"
                sx={{ px: 2, py: 2.5, fontSize: '1rem', fontWeight: 600, minWidth: 120 }}
              />
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
              {error}
            </Alert>
          )}

          {filteredOrders.length === 0 && orders.length > 0 ? (
            <Fade in timeout={600}>
              <Paper
                sx={{
                  p: 8,
                  textAlign: 'center',
                  borderRadius: 4,
                  bgcolor: 'white',
                  boxShadow: 3
                }}
              >
                <SearchIcon sx={{ fontSize: 80, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom fontWeight={600}>
                  No Orders Found
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                  No orders match your search query. Try a different order number.
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => setSearchQuery('')}
                  size="large"
                  sx={{ px: 4, py: 1.5, borderRadius: 3 }}
                >
                  Clear Search
                </Button>
              </Paper>
            </Fade>
          ) : filteredOrders.length === 0 ? (
            <Fade in timeout={600}>
              <Paper
                sx={{
                  p: 8,
                  textAlign: 'center',
                  borderRadius: 4,
                  bgcolor: 'white',
                  boxShadow: 3
                }}
              >
                <ShoppingBagIcon sx={{ fontSize: 80, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom fontWeight={600}>
                  No Orders Yet
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                  You haven't placed any orders yet. Start shopping to see your orders here!
                </Typography>
                <Button
                  variant="contained"
                  component={RouterLink}
                  to={`/${websitePath}`}
                  size="large"
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 3,
                    background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                    boxShadow: `0 4px 12px ${theme.palette.primary.main}40`
                  }}
                >
                  Start Shopping
                </Button>
              </Paper>
            </Fade>
          ) : (
            <Box ref={ordersRef}>
              {filteredOrders.map((order, index) => (
                <Card
                  key={order.id}
                  className="order-card"
                  sx={{
                    mb: 3,
                    borderRadius: 4,
                    boxShadow: 4,
                    overflow: 'hidden',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: 8,
                      transform: 'translateY(-4px)'
                    }
                  }}
                >
                  {/* Order Header */}
                  <Box
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      p: 2.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexWrap: 'wrap',
                      gap: 2
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <ReceiptIcon sx={{ fontSize: 32 }} />
                      <Box>
                        <Typography variant="h6" fontWeight={700}>
                          Order #{order.orderNumber}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Placed on {formatDate(order.orderDate || order.createdAt)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip
                        icon={getStatusIcon(order.status)}
                        label={order.status || 'PENDING'}
                        color={getStatusColor(order.status)}
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.2)',
                          color: 'white',
                          fontWeight: 600,
                          '& .MuiChip-icon': {
                            color: 'white'
                          }
                        }}
                      />
                      <Typography variant="h6" fontWeight={700}>
                        {formatCurrency(order.total)}
                      </Typography>
                    </Box>
                  </Box>

                  <CardContent sx={{ p: 3 }}>
                    {/* Order Items */}
                    {order.items && order.items.length > 0 ? (
                      <Accordion defaultExpanded={index === 0} sx={{ boxShadow: 'none', mb: 2 }}>
                        <AccordionSummary
                          expandIcon={<ExpandMoreIcon />}
                          sx={{
                            bgcolor: 'grey.50',
                            borderRadius: 2,
                            '&:hover': { bgcolor: 'grey.100' }
                          }}
                        >
                          <Typography variant="body1" fontWeight={600}>
                            {order.items.length} {order.items.length === 1 ? 'Item' : 'Items'}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          <TableContainer>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell><strong>Product</strong></TableCell>
                                  <TableCell align="right"><strong>Quantity</strong></TableCell>
                                  <TableCell align="right"><strong>Price</strong></TableCell>
                                  <TableCell align="right"><strong>Total</strong></TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {order.items.map((item, itemIndex) => (
                                  <TableRow key={itemIndex}>
                                    <TableCell>
                                      <Typography variant="body2" fontWeight={500}>
                                        {getProductName(item)}
                                      </Typography>
                                    </TableCell>
                                    <TableCell align="right">{item.quantity}</TableCell>
                                    <TableCell align="right">
                                      {formatCurrency(item.unitPrice)}
                                    </TableCell>
                                    <TableCell align="right">
                                      <strong>{formatCurrency(item.total)}</strong>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </AccordionDetails>
                      </Accordion>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        No items found for this order
                      </Typography>
                    )}

                    <Divider sx={{ my: 2 }} />

                    {/* Order Summary */}
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Payment Method
                          </Typography>
                          <Typography variant="body1" fontWeight={600}>
                            {order.paymentMethod || 'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Payment Status
                          </Typography>
                          <Chip
                            label={order.paymentStatus || 'PENDING'}
                            size="small"
                            color={order.paymentStatus === 'PAID' ? 'success' : 'warning'}
                            sx={{ fontWeight: 600 }}
                          />
                        </Box>
                      </Grid>
                      {order.expectedShippingDate && (
                        <Grid item xs={12} sm={6}>
                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Expected Delivery
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {formatDate(order.expectedShippingDate)}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                      {order.shippingDate && (
                        <Grid item xs={12} sm={6}>
                          <Box>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              Shipped On
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {formatDate(order.shippingDate)}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Grid>

                    {/* Order Totals */}
                    <Box
                      sx={{
                        mt: 3,
                        p: 2,
                        bgcolor: 'grey.50',
                        borderRadius: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Subtotal: {formatCurrency(order.subtotal)}
                      </Typography>
                      {order.taxAmount > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          Tax: {formatCurrency(order.taxAmount)}
                        </Typography>
                      )}
                      {order.shippingCost > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          Shipping: {formatCurrency(order.shippingCost)}
                        </Typography>
                      )}
                      <Typography variant="h6" fontWeight={700} color="primary.main">
                        Total: {formatCurrency(order.total)}
                      </Typography>
                    </Box>

                    {order.notes && (
                      <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
                        <Typography variant="body2" color="info.dark">
                          <strong>Note:</strong> {order.notes}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default CustomerOrdersPage;

